import { logHttp } from './HttpApi';

export enum LogLevel {
    Trace = 0,
    Debug = 1,
    Information = 2,
    Warning = 3,
    Error = 4,
    Critical = 5,
    None = 6,
}

export class LogEvent {
    eventId: number;
    eventName: string | null;

    constructor(eventId: number, eventName: string | null = null) {
        this.eventId = eventId;
        this.eventName = eventName;
    }
}

export class LogEntry {
    eventId: number | null;
    eventName: string | null;

    level: LogLevel;
    message: string;

    parameters: any[] = [];

    constructor(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        ...parameters: any[]
    ) {
        this.eventId = event?.eventId ?? null;
        this.eventName = event?.eventName ?? null;
        this.level = level;
        this.message = message;
        this.parameters = parameters;
    }
}

export class LoggerOptions {
    debug: boolean = false;
    minLevel?: LogLevel;
}

export class Logger {
    options: LoggerOptions;
    private faildedLogs: LogEntry[] = [];

    constructor(options: LoggerOptions = new LoggerOptions()) {
        this.options = options;
    }

    log(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        data: any
    ) {
        return this.send(level, message, event, data);
    }

    logDebug(message: string, event: LogEvent | null = null, data: any) {
        return this.send(LogLevel.Debug, message, event, data);
    }

    logError(message: string, event: LogEvent | null = null, data: any) {
        return this.send(LogLevel.Error, message, event, data);
    }

    logInformation(message: string, event: LogEvent | null = null, data: any) {
        return this.send(LogLevel.Information, message, event, data);
    }

    async sendStackedLogMessages() {
        const faildedLogsCopy: LogEntry[] = [...this.faildedLogs];
        this.faildedLogs = [];

        for (const logEntry of faildedLogsCopy) {
            try {
                await logHttp.post('/api/logging/log', logEntry);

                this.options.debug &&
                    console.log(`Successfuly sent log message.`, logEntry);
            } catch (error) {
                this.faildedLogs.push(logEntry);

                this.options.debug &&
                    console.error(
                        `Failed to send stacked log message.`,
                        logEntry,
                        error
                    );
            }
        }
    }

    localLog(
        message: string,
        event: LogEvent | null = null,
        amount: number = 0,
        banknoteId: string | null = ''
    ) {
        const eventName = event ? `Event: ${event.eventName}` : '';
         var tail = amount && banknoteId?`, Amount: ${amount}, BanknoteId: ${banknoteId}`:''
        console.log(
            `Component: CommonApp, ${eventName}, Message: ${message}${tail}`
        );
    }

    private send(
        level: LogLevel,
        message: string,
        event: LogEvent | null = null,
        ...parameters: any[]
    ) {
        const logEntry = new LogEntry(
            level,
            //TODO: think about this, how to organize interface, do all log messages need to add '{data}' string like Serilog message template, or do it globally?
            `${message} {dataJson}`,
            event,
            ...parameters
        );

        if (this.options.minLevel && this.options.minLevel > level) {
            this.options.debug &&
                console.warn(
                    `Skipping logEntry, because minLevel is ${this.options.minLevel}`,
                    logEntry,
                    this.options
                );
            return Promise.resolve();
        }

        this.options.debug && console.log(`Sending log message.`, logEntry);

        return logHttp.post('/api/logging/log', logEntry).then(
            (_) => {
                this.options.debug &&
                    console.log(`Successfuly sent log message.`, logEntry);
            },
            (error: any) => {
                logEntry.parameters[0].timestamp = new Date().toISOString();
                this.faildedLogs.push(logEntry);

                this.options.debug &&
                    console.error(
                        `Failed to send log message.`,
                        logEntry,
                        error
                    );
            }
        );
    }
}

export const logger = new Logger({
    debug: false,
    minLevel: LogLevel.Information,
});

export class LogEvents {
    static WizardStepChanged: LogEvent = new LogEvent(98, 'WizardStepChanged');
    static LanguageSelected: LogEvent = new LogEvent(99, 'LanguageSelected');
    static ClosingApp: LogEvent = new LogEvent(100, 'CloseApp');

    static CashReceiverStarted: LogEvent = new LogEvent(
        100,
        'CashReceiverStarted'
    );
    static CashReceiverProccessed: LogEvent = new LogEvent(
        101,
        'CashReceiverProccessed'
    );

    static TransactionCreateFailed: LogEvent = new LogEvent(
        110,
        'TransactionCreateFailed'
    );
    static TransactionUpdateFailed: LogEvent = new LogEvent(
        111,
        'TransactionUpdateFailed'
    );
    static TransactionCompleteFailed: LogEvent = new LogEvent(
        112,
        'TransactionCompleteFailed'
    );
    static TransactionChanged: LogEvent = new LogEvent(
        113,
        'TransactionChanged'
    );
    static ReturnCash: LogEvent = new LogEvent(114, 'ReturnCash');
    static BillCheckFailed: LogEvent = new LogEvent(115, 'BillCheckFailed');
    static StackCash: LogEvent = new LogEvent(116, 'StackCash');
    static SendAmount: LogEvent = new LogEvent(117, 'SendAmount');

    static ReceiptGenerateFailed: LogEvent = new LogEvent(
        200,
        'ReceiptGenerateFailed'
    );
    static ReceiptPrintFailed: LogEvent = new LogEvent(
        200,
        'ReceiptPrintFailed'
    );

    static UnhandledScriptError: LogEvent = new LogEvent(
        300,
        'UnhandledScriptError'
    );
    static AppNotClosed: LogEvent = new LogEvent(301, 'AppNotClosed');

    static MessageBrokerMessageReceived: LogEvent = new LogEvent(
        302,
        'MessageBrokerMessageReceived'
    );

    static MessageBrokerTimeoutMessageNotReceived: LogEvent = new LogEvent(
        303,
        'MessageBrokerTimeoutMessageNotReceived'
    );

    static InternetConnectionLost: LogEvent = new LogEvent(
        600,
        'InternetConnectionLost'
    );
    static InternetConnectionRestored: LogEvent = new LogEvent(
        601,
        'InternetConnectionRestored'
    );
    static RemoveAmount: LogEvent = new LogEvent(
        602,
        'RemoveAmountInCamunda'
    );

    static Discrepancy: LogEvent = new LogEvent(
        603,
        'DiscrepancyAfterCollection'
    );

    static NoDiscrepancy: LogEvent = new LogEvent(
        604,
        'NoDiscrepancyAfterCollection'
    );
}
